import { useState } from "react";
import { api } from "@/lib/api";

/**
 * Modal de creación de personajes desde briefing libre (IA).
 *
 * El usuario escribe un briefing en lenguaje natural describiendo uno o varios
 * personajes. La IA (Claude Sonnet 4.5 vía Emergent Key) detecta cuántos hay y
 * los devuelve con TODOS los campos del modelo (nombre, categoría, edad,
 * físico, personalidad, background, motivación, conflicto, voz, relaciones,
 * notas).
 *
 * El backend pasa a la IA la lista de nombres ya registrados para que no
 * duplique personajes. Si la IA aún así propone un duplicado, se filtra en
 * servidor y se devuelve en `skipped_duplicates`.
 *
 * El modal NO persiste — muestra los personajes en preview editable, y al
 * confirmar llama al callback `onAddAll(characters)` que los añade uno a uno
 * via el endpoint POST estándar de characters.
 *
 * VIVE FUERA del editor protegido (`components/screenplay/**`). Se monta desde
 * el panel "Personajes" mediante un botón externo.
 */
const FIELDS = [
  { id: "age",           label: "Edad" },
  { id: "physical",      label: "Físico" },
  { id: "personality",   label: "Personalidad" },
  { id: "background",    label: "Background" },
  { id: "motivation",    label: "Motivación" },
  { id: "conflict",      label: "Conflicto" },
  { id: "voice",         label: "Voz / tono" },
  { id: "relationships", label: "Relaciones" },
  { id: "notes",         label: "Notas" },
];

const CATEGORY_OPTIONS = [
  { id: "protagonista", label: "Protagonista" },
  { id: "secundario", label: "Secundario" },
  { id: "esporadico", label: "Secundario esporádico" },
  { id: "figuracion", label: "Figuración con frase" },
];

const EXAMPLE = `Necesito 3 personajes para un drama familiar:

Marta, una doctora de 50 años obsesionada con salvar a todos. Su hijo Adrián, 16 años, en silencio depresivo desde la muerte del padre. Y Lorca, un policía corrupto que les chantajea por una deuda antigua.`;

export default function CharacterBriefingModal({ projectId, onClose, onAddAll }) {
  const [briefing, setBriefing] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [draft, setDraft] = useState(null); // { characters, count }
  const [adding, setAdding] = useState(false);

  const submit = async () => {
    setLoading(true); setError(""); setDraft(null);
    try {
      const r = await api.post(
        `/icaa/projects/${projectId}/screenplay/characters/from-briefing`,
        { briefing },
      );
      setDraft(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Error generando personajes");
    } finally { setLoading(false); }
  };

  const updateField = (i, field, value) => {
    if (!draft) return;
    const chars = [...draft.characters];
    chars[i] = { ...chars[i], [field]: value };
    setDraft({ ...draft, characters: chars });
  };

  const removeChar = (i) => {
    if (!draft) return;
    const chars = draft.characters.filter((_, idx) => idx !== i);
    setDraft({ ...draft, characters: chars, count: chars.length });
  };

  const addAll = async () => {
    if (!draft || draft.characters.length === 0) return;
    setAdding(true);
    try {
      await onAddAll(draft.characters);
      onClose();
    } catch (e) {
      setError(e?.message || "Error añadiendo personajes al guion");
    } finally { setAdding(false); }
  };

  return (
    <div className="fixed inset-0 z-[55] flex items-center justify-center bg-black/55 px-4 py-6"
         data-testid="character-briefing-modal"
         onClick={onClose}>
      <div className="flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-2xl"
           onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-start justify-between border-b border-stone-200 px-6 py-4">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#941620]">
              ✨ Crear personajes desde briefing
            </p>
            <h2 className="mt-0.5 font-serif text-xl text-[#2C2B29]">
              Describe a tu(s) personaje(s) y la IA los completa
            </h2>
            <p className="mt-1 text-[11px] text-stone-500">
              Detecta automáticamente cuántos personajes describes y rellena nombre, edad, físico, personalidad, motivación, conflicto, voz, relaciones y notas.
            </p>
          </div>
          <button onClick={onClose} data-testid="briefing-modal-close"
                  className="rounded-full border border-stone-300 px-2 py-0.5 text-stone-500 hover:border-[#941620] hover:text-[#941620]">
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {!draft ? (
            <div className="p-6">
              <label className="text-[10px] font-semibold uppercase tracking-[0.22em] text-stone-600">
                Briefing
              </label>
              <textarea
                value={briefing}
                onChange={(e) => setBriefing(e.target.value)}
                placeholder={EXAMPLE}
                autoFocus
                rows={10}
                data-testid="briefing-textarea"
                className="mt-1.5 w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm leading-relaxed outline-none focus:border-[#941620]"
              />
              <p className="mt-2 text-[11px] text-stone-500">
                💡 Cuanto más concreto seas (edad, contexto, conflicto), más cinematográficos serán los personajes.
                Si dices "Marta y Adrián" la IA generará 2. Si describes uno solo, generará 1.
              </p>
              {error && (
                <p className="mt-2 rounded bg-rose-50 px-3 py-2 text-[12px] text-rose-700">
                  {error}
                </p>
              )}
              <div className="mt-4 flex items-center justify-between">
                <button onClick={() => setBriefing(EXAMPLE)}
                        data-testid="briefing-load-example"
                        className="text-[11px] uppercase tracking-[0.22em] text-stone-500 underline-offset-2 hover:text-[#941620] hover:underline">
                  Cargar ejemplo
                </button>
                <button
                  onClick={submit}
                  disabled={loading || briefing.trim().length < 20}
                  data-testid="briefing-generate"
                  className="rounded-full bg-[#941620] px-6 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-white transition hover:bg-[#2C2B29] disabled:opacity-40">
                  {loading ? "Generando…" : "✨ Generar personajes"}
                </button>
              </div>
              {loading && (
                <p className="mt-2 text-center text-[11px] uppercase tracking-[0.18em] text-stone-500">
                  Claude está creando los personajes (10-30 s)…
                </p>
              )}
            </div>
          ) : (
            <div className="p-6">
              <div className="mb-3 flex items-center justify-between">
                <p className="text-[11px] uppercase tracking-[0.22em] text-stone-600">
                  ✓ {draft.count} {draft.count === 1 ? "personaje generado" : "personajes generados"} · revisa y edita antes de añadir
                </p>
                <button onClick={() => { setDraft(null); setError(""); }}
                        data-testid="briefing-back"
                        className="text-[10px] uppercase tracking-[0.22em] text-stone-500 hover:text-[#941620]">
                  ← Volver al briefing
                </button>
              </div>

              {/* Aviso si la IA encontró duplicados y los descartó. */}
              {draft.skipped_duplicates && draft.skipped_duplicates.length > 0 && (
                <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-[12px] text-amber-900"
                     data-testid="briefing-skipped-duplicates">
                  ⚠ {draft.skipped_duplicates.length} personaje{draft.skipped_duplicates.length === 1 ? "" : "s"} ya existía
                  {draft.skipped_duplicates.length === 1 ? "" : "n"} en la matriz y no se
                  duplicará{draft.skipped_duplicates.length === 1 ? "" : "n"}:{" "}
                  <span className="font-mono uppercase tracking-wider">
                    {draft.skipped_duplicates.join(" · ")}
                  </span>
                </div>
              )}

              {/* Si todo lo que pidió el usuario ya estaba registrado, no hay
                  nada que mostrar. */}
              {draft.characters.length === 0 && (
                <div className="rounded-lg border border-dashed border-stone-300 p-6 text-center text-[12px] text-stone-500"
                     data-testid="briefing-empty">
                  Todos los personajes del briefing ya estaban registrados.
                  No hay nada nuevo que añadir.
                </div>
              )}

              <ul className="space-y-3" data-testid="briefing-character-list">
                {draft.characters.map((c, i) => (
                  <li key={i} className="rounded-xl border border-stone-200 bg-[#FBFAF4] p-4"
                      data-testid={`briefing-char-${i}`}>
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <input
                        value={c.name || ""}
                        onChange={(e) => updateField(i, "name", e.target.value.toUpperCase())}
                        className="flex-1 rounded border border-stone-300 bg-white px-3 py-1.5 font-serif text-base font-bold uppercase tracking-wide text-[#2C2B29] outline-none focus:border-[#941620]"
                      />
                      <select
                        value={c.category || "secundario"}
                        onChange={(e) => updateField(i, "category", e.target.value)}
                        data-testid={`briefing-char-category-${i}`}
                        title="Categoría dramatúrgica"
                        className="rounded border border-stone-300 bg-white px-2 py-1 text-[11px] uppercase tracking-wider outline-none focus:border-[#941620]"
                      >
                        {CATEGORY_OPTIONS.map((opt) => (
                          <option key={opt.id} value={opt.id}>{opt.label}</option>
                        ))}
                      </select>
                      <button onClick={() => removeChar(i)}
                              data-testid={`briefing-char-remove-${i}`}
                              title="Eliminar este personaje"
                              className="rounded-full border border-stone-300 px-2 py-0.5 text-[10px] uppercase tracking-[0.22em] text-stone-500 hover:border-rose-500 hover:text-rose-600">
                        🗑
                      </button>
                    </div>
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                      {FIELDS.map(({ id, label }) => (
                        <div key={id} className={id === "physical" || id === "personality" || id === "background" || id === "relationships" || id === "notes" ? "md:col-span-2" : ""}>
                          <label className="text-[9px] font-semibold uppercase tracking-[0.25em] text-stone-500">
                            {label}
                          </label>
                          <textarea
                            value={c[id] || ""}
                            onChange={(e) => updateField(i, id, e.target.value)}
                            rows={id === "age" ? 1 : (id === "personality" || id === "background" || id === "physical" ? 2 : 1)}
                            className="mt-0.5 w-full resize-none rounded border border-stone-200 bg-white px-2 py-1 text-[12px] leading-snug outline-none focus:border-[#941620]"
                          />
                        </div>
                      ))}
                    </div>
                  </li>
                ))}
              </ul>

              {error && (
                <p className="mt-3 rounded bg-rose-50 px-3 py-2 text-[12px] text-rose-700">
                  {error}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {draft && (
          <div className="flex items-center justify-between border-t border-stone-200 bg-[#FBFAF4] px-6 py-3">
            <button onClick={onClose}
                    className="text-[11px] uppercase tracking-[0.22em] text-stone-500 hover:text-[#941620]">
              Cancelar
            </button>
            <button onClick={addAll} disabled={adding || draft.characters.length === 0}
                    data-testid="briefing-add-all"
                    className="rounded-full bg-[#26775E] px-6 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-white transition hover:bg-[#2C2B29] disabled:opacity-40">
              {adding ? "Añadiendo…" : `+ Añadir ${draft.characters.length} al guion`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
